import React from 'react';
import Card from '../../../Presentation/components/ui/Card';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../Presentation/components/ui/AppTableHeaderCell';
import {AppText, AppBodyText, AppSectionTitle} from '../../../Presentation/components/ui/Typography';
import Button from '../../../Presentation/components/ui/Button';
import ConfirmationDialog from '../../../Presentation/components/ui/ConfirmationDialog';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';
import {usePaymentsUIStore} from '../store/paymentsUIStore';

interface BankTransferRecord {
  id: number;
  invoice_number: string;
  store: {
    id: number;
    name: string;
    phone: string;
  };
  marketer: {
    id: number;
    name: string;
  };
  total_amount: number;
  transfer_image_url: string;
  created_at: string;
  notes: string;
  payment_status: string;
}

interface Props {
  data: BankTransferRecord[];
  loading?: boolean;
  onApprove: (invoiceId: number | string) => Promise<void> | void;
  onReject: (invoiceId: number | string) => Promise<void> | void;
  onRefresh?: () => void;
  exportable?: boolean;
}

const BankTransferReviewTable: React.FC<Props> = ({data, onApprove, onReject, onRefresh, exportable = false}) => {
  const {
    transferNotes: notes, updateTransferNote: setNotes,
    actionLoading, setActionLoading,
    confirmDialog, openConfirmDialog, closeConfirmDialog
  } = usePaymentsUIStore();

  // Export configuration
  const {createConfig} = useExportConfig<BankTransferRecord>();

  const exportConfig = createConfig([
    {key: 'id', title: 'معرف التحويل'},
    {key: 'invoice_number', title: 'رقم الفاتورة'},
    {key: 'store', title: 'اسم المتجر', formatter: ExportFormatters.storeName},
    {key: 'store', title: 'هاتف المتجر', formatter: ExportFormatters.storePhone},
    {key: 'marketer', title: 'اسم المسوق', formatter: ExportFormatters.marketerName},
    {key: 'total_amount', title: 'المبلغ الإجمالي', formatter: ExportFormatters.currencySARExport},
    {key: 'created_at', title: 'تاريخ الإنشاء', formatter: ExportFormatters.dateTime},
    {key: 'notes', title: 'ملاحظات'},
    {key: 'payment_status', title: 'حالة الدفع', formatter: ExportFormatters.paymentStatus}
  ], 'تحويلات_بنكية', 'تصدير سجلات التحويلات البنكية', 'تصدير جميع سجلات التحويلات البنكية مع التفاصيل المالية');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: data,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  const handleAction = async (type: 'approve' | 'reject', invoiceId: number | string, invoiceNumber: string) => {
    openConfirmDialog(type, invoiceId, invoiceNumber);
  };

  const confirmAction = async () => {
    if (!confirmDialog.type || !confirmDialog.invoiceId) {return;}

    const actionType = confirmDialog.type;
    setActionLoading({[actionType]: true});

    try {
      if (actionType === 'approve') {
        await onApprove(confirmDialog.invoiceId);
      } else {
        await onReject(confirmDialog.invoiceId);
      }

      // Clear notes for this invoice
      if (confirmDialog.invoiceId) {
        setNotes(confirmDialog.invoiceId.toString(), '');
      }
    } catch (error) {
    } finally {
      setActionLoading({[actionType]: false});
      closeConfirmDialog();
    }
  };

  const columns: TableColumn<BankTransferRecord>[] = [
    {
      key: 'invoice_number',
      title: <AppTableHeaderCell label="رقم الفاتورة" />,
      render: (record) => (
        <AppText className="font-medium text-brand-600">
          {record.invoice_number}
        </AppText>
      )
    },
    {
      key: 'store',
      title: <AppTableHeaderCell label="المتجر" />,
      render: (record) => (
        <div>
          <AppText className="font-medium">{record.store.name}</AppText>
          <AppBodyText className="text-gray-500">{record.store.phone}</AppBodyText>
        </div>
      )
    },
    {
      key: 'marketer',
      title: <AppTableHeaderCell label="المسوق" />,
      render: (record) => (
        <AppText>{record.marketer.name}</AppText>
      )
    },
    {
      key: 'total_amount',
      title: <AppTableHeaderCell label="المبلغ" />,
      render: (record) => (
        <AppText className="font-semibold text-green-600">
          {formatCurrency(record.total_amount)}
        </AppText>
      )
    },
    {
      key: 'transfer_image_url',
      title: <AppTableHeaderCell label="صورة التحويل" />,
      render: (record) => (
        <div className="flex justify-center">
          {record.transfer_image_url ? (
            <img
              src={record.transfer_image_url}
              alt="Transfer receipt"
              className="w-16 h-16 object-cover rounded border cursor-pointer hover:scale-110 transition-transform"
              onClick={() => window.open(record.transfer_image_url, '_blank')}
            />
          ) : (
            <AppText className="text-gray-400">لا توجد صورة</AppText>
          )}
        </div>
      )
    },
    {
      key: 'created_at',
      title: <AppTableHeaderCell label="تاريخ الطلب" />,
      render: (record) => (
        <AppBodyText>
          {new Date(record.created_at).toLocaleDateString('en-US')}
        </AppBodyText>
      )
    },
    {
      key: 'notes',
      title: <AppTableHeaderCell label="ملاحظات المراجعة" />,
      render: (record) => (
        <textarea
          value={notes[record.id.toString()] || ''}
          onChange={(e) => setNotes(record.id.toString(), e.target.value)}
          placeholder="أضف ملاحظات المراجعة..."
          className="w-full p-2 border border-gray-300 rounded text-sm resize-none"
          rows={2}
        />
      )
    },
    {
      key: 'actions',
      title: <AppTableHeaderCell label="الإجراءات" />,
      render: (record) => (
        <div className="flex gap-2">
          <Button
            size="small"
            onClick={() => handleAction('approve', record.id, record.invoice_number)}
            disabled={actionLoading.approve}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {actionLoading.approve ? 'جاري...' : 'موافقة'}
          </Button>
          <Button
            size="small"
            variant="outline"
            onClick={() => handleAction('reject', record.id, record.invoice_number)}
            disabled={actionLoading.reject}
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            {actionLoading.reject ? 'جاري...' : 'رفض'}
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <AppSectionTitle className="font-bold text-gray-900">
              مراجعة التحويلات البنكية
            </AppSectionTitle>
            <AppBodyText className="text-gray-600">
              التحويلات البنكية قيد المراجعة ({data.length})
            </AppBodyText>
          </div>
          {/* Export Button */}
          {exportable && (
            <ExportButton
              onExport={handleExport}
              isExporting={isExporting}
              canExport={canExport}
              recordCount={recordCount}
              showDropdown={true}
            />
          )}
          {onRefresh && (
            <Button
              variant="outline"
              onClick={onRefresh}
              className="flex items-center gap-2"
            >
              تحديث
            </Button>
          )}
        </div>

        <AppTable
          columns={columns}
          data={data}
          rowKey={(record) => `transfer-${record.id}`}
          emptyText="لا توجد تحويلات قيد المراجعة"
          showPaginationInfo={false}
          showPageSizeSelector={false}
        />
      </Card>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={confirmAction}
        title={confirmDialog.type === 'approve' ? 'تأكيد الموافقة' : 'تأكيد الرفض'}
        message={
          confirmDialog.type === 'approve'
            ? `هل أنت متأكد من الموافقة على التحويل للفاتورة ${confirmDialog.invoiceNumber}؟`
            : `هل أنت متأكد من رفض التحويل للفاتورة ${confirmDialog.invoiceNumber}؟`
        }
        confirmText={confirmDialog.type === 'approve' ? 'موافقة' : 'رفض'}
        confirmButtonVariant={confirmDialog.type === 'approve' ? 'primary' : 'danger'}
      />
    </div>
  );
};

export default BankTransferReviewTable;
